import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from './Button';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // Log to error reporting service (e.g., Sentry)
    // Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } });
    
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  public componentDidUpdate(prevProps: Props) {
    // A parent retry can provide a fresh child tree after the fallback action.
    if (this.state.hasError && prevProps.children !== this.props.children) {
      this.setState({ hasError: false, error: null });
    }
  }

  public reset = () => {
    this.setState({ hasError: false, error: null });
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-background">
          <div className="text-center max-w-md p-8">
            <h1 className="text-destructive mb-4 font-display text-page-title">Oops!</h1>
            <p className="text-muted-foreground mb-6">
              Something went wrong. Don't worry, our team has been notified.
            </p>
            {this.state.error && (
              <details open className="text-left bg-card rounded-lg p-4 mb-6 border border-border">
                <summary className="cursor-pointer text-muted-foreground mb-2 font-medium">
                  Error Details ({this.state.error.name}: {this.state.error.message})
                </summary>
                <pre className="text-xs text-destructive whitespace-pre-wrap font-mono overflow-auto max-h-60 p-2 bg-muted/20 rounded">
                  {this.state.error.stack || this.state.error.toString()}
                </pre>
              </details>
            )}
            <Button
              onClick={this.reset}
            >
              Try Again
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
